import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import type { BlockType } from '@unifyapps/defs/types/block';
import type { CardAppearance, CardComponentType } from '@unifyapps/defs/types/blocks';
import SvgChevronDown from '@unifyapps/icons/outline/ChevronDown';
import { UA_INTERFACE_ENABLE_HOVER_EFFECT } from '@unifyapps/style/themes/consts';
import { Box } from '@unifyapps/ui/components/Box';
import { Divider } from '@unifyapps/ui/components/Divider';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Stack } from '@unifyapps/ui/components/Stack';
import useForkRef from '@unifyapps/ui/hooks/useForkRef';
import { clsx } from 'clsx';
import omit from 'lodash/omit';
import type {
  ForwardRefExoticComponent,
  PropsWithChildren,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  RefAttributes,
  RefCallback,
  SyntheticEvent,
} from 'react';
import { Children, forwardRef, useCallback, useEffect, useMemo } from 'react';
import { useCollapse } from 'react-collapsed';
import type { LongPressHandlers } from 'use-long-press';
import { isEventHandledByBlock } from '../utils/eventHandling';
import { useCardAppearance } from './useCardAppearance';

enum CardLayoutSlots {
  Header = 'HEADER',
  Body = 'BODY',
  Footer = 'FOOTER',
}

type SlotProps = { name: CardLayoutSlots; id?: string };

function Slot(_props: PropsWithChildren<SlotProps>): null {
  return null;
}

type CardLayoutProps = {
  children: ReactElement<unknown>[];
  appearance?: CardAppearance;
  attributesAndEventListeners?: Record<string, unknown>;
  onClick?: (e: SyntheticEvent) => void;
  onMouseEnter?: (e: ReactMouseEvent<HTMLElement>) => void;
  longPressProps?: LongPressHandlers;
  hasClickEvent?: boolean;
  collapsibleParams: {
    isCollapsed: boolean;
    setIsCollapsed: (isCollapsed: boolean) => void;
    enabled: boolean;
    defaultExpanded: boolean;
  };
  blockRef: RefCallback<HTMLElement | null> | null;
  block: BlockType<CardComponentType>;
  onSetExpandedRef?: (setExpanded: (expanded: boolean) => void) => void;
  /** When true, onTransitionStateChange will not call setIsCollapsed (used when sync updates UI via setExpanded) */
  isProgrammaticCollapseChangeRef: React.RefObject<boolean>;
};

type CardLayoutComponent = ForwardRefExoticComponent<
  CardLayoutProps & RefAttributes<HTMLDivElement>
> & {
  Slot: typeof Slot;
};

const CardLayout = forwardRef<HTMLDivElement, CardLayoutProps>(
  (
    {
      children,
      appearance,
      attributesAndEventListeners,
      onClick,
      onMouseEnter,
      longPressProps,
      hasClickEvent,
      collapsibleParams,
      blockRef,
      block,
      onSetExpandedRef,
      isProgrammaticCollapseChangeRef,
      ...props
    },
    ref,
  ): ReactElement<unknown> => {
    const { setIsCollapsed, isCollapsed } = collapsibleParams;

    const { showDivider } = appearance ?? {};

    const { getCollapseProps, getToggleProps, setExpanded } = useCollapse({
      defaultExpanded: collapsibleParams.enabled ? !collapsibleParams.isCollapsed : true,
      easing: 'linear',
      duration: 200,
      onTransitionStateChange(state) {
        // skip updating block state when change came from builder sync (setExpandedRef)
        if (isProgrammaticCollapseChangeRef.current) {
          return;
        }
        switch (state) {
          case 'expandStart':
            setIsCollapsed(false);
            break;
          case 'collapseEnd':
            setIsCollapsed(true);
            break;
          default:
        }
      },
    }) as {
      getCollapseProps: () => Record<string, unknown>;
      getToggleProps: () => Record<string, unknown>;
      setExpanded: (expanded: boolean) => void;
    };

    // expose setExpanded to parent component,
    // this is done so that when trigger block method is used on card, we can update the block state as well as update the useCollapse hook state
    useEffect(() => {
      onSetExpandedRef?.(setExpanded);
    }, [onSetExpandedRef, setExpanded]);

    const { rootClassName, rootStyle, rootAndChildrenStyles, rootAndChildrenClassName } =
      useCardAppearance({
        appearance,
      });

    const { finalStyle } = useCustomStyles({
      customCSS: block.additional?.customCSS,
      customStyles: block.component?.appearance?.customStyles,
      style: rootStyle,
    });

    const childrenArr = Children.toArray(children);

    const headerSlot = childrenArr.find(
      (child: ReactElement<SlotProps>) => child.props.name === CardLayoutSlots.Header,
    ) as ReactElement<{ children: ReactElement<unknown> }> | undefined;

    const bodySlot = childrenArr.find(
      (child: ReactElement<SlotProps>) => child.props.name === CardLayoutSlots.Body,
    ) as ReactElement<{ children: ReactElement<unknown> }> | undefined;

    const footerSlot = childrenArr.find(
      (child: ReactElement<SlotProps>) => child.props.name === CardLayoutSlots.Footer,
    ) as ReactElement<{ children: ReactElement<unknown> }> | undefined;

    const isHeaderPresent = Boolean(headerSlot?.props.children);
    const isBodyPresent = Boolean(bodySlot?.props.children);
    const isFooterPresent = Boolean(footerSlot?.props.children);

    // Wrap the toggle click handler to skip if the event was already handled by a child block
    const handleToggleClick = useCallback(
      (e: ReactMouseEvent) => {
        // Skip collapse toggle if a child block (e.g., IconButton, Checkbox) already handled this event
        if (isEventHandledByBlock(e)) {
          return;
        }
        // Get the original onClick from react-collapsed and invoke it
        const toggleProps = getToggleProps() as { onClick?: (e: ReactMouseEvent) => void };
        toggleProps.onClick?.(e);
      },
      [getToggleProps],
    );

    const toggleCollapseProps = useMemo(() => {
      if (!collapsibleParams.enabled) {
        return {};
      }
      const baseProps = getToggleProps();
      return {
        ...baseProps,
        // Override onClick with our wrapper that checks for handled events
        onClick: handleToggleClick,
      };
    }, [collapsibleParams.enabled, getToggleProps, handleToggleClick]);

    const collapsibleProps = useMemo(() => {
      const props = getCollapseProps();
      // Prevent broken ARIA reference
      const shouldOmitAria = !isHeaderPresent || !collapsibleParams.enabled;
      return shouldOmitAria ? omit(props, ['aria-labelledby']) : props;
    }, [getCollapseProps, isHeaderPresent, collapsibleParams.enabled]);

    const rootRef = useForkRef(ref, blockRef);

    return (
      <Box
        {...props}
        className={clsx('flex flex-col', rootClassName, {
          [`${UA_INTERFACE_ENABLE_HOVER_EFFECT} cursor-pointer`]: Boolean(hasClickEvent),
        })}
        {...attributesAndEventListeners}
        {...longPressProps}
        enableKeyboardActivation={Boolean(hasClickEvent)}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        ref={rootRef}
        style={finalStyle}
      >
        {isHeaderPresent ? (
          <Stack
            alignItems="center"
            className="gap-xs"
            direction="row"
            justifyContent="space-between"
            {...toggleCollapseProps}
          >
            {headerSlot?.props.children}
            {collapsibleParams.enabled ? (
              <IconButton
                className="me-md rtl:-scale-x-100"
                color="neutral"
                IconNode={
                  <SvgChevronDown
                    className={clsx('shrink-0 size-4 transform transition-transform duration-200', {
                      '-rotate-90': isCollapsed,
                    })}
                  />
                }
                size="sm"
                variant="soft"
              />
            ) : null}
          </Stack>
        ) : null}

        {/*
      transition property is height.
      shrink-1 will make child height as 0  in collapsed state, 0 to 0 animation will not work so child should not be shrunk to 0
      if shrink-1 is not added child's height will be constant, in that case 0 - > child height animation will take place 
      */}
        <Stack
          className={clsx(rootAndChildrenClassName, 'min-h-0 grow')}
          direction="column"
          style={rootAndChildrenStyles}
          {...collapsibleProps}
        >
          {isCollapsed ? null : (
            <>
              {showDivider && isHeaderPresent ? <Divider /> : null}
              {isBodyPresent ? (
                <Box className={clsx('min-h-0 grow')}>{bodySlot?.props.children}</Box>
              ) : null}
              {showDivider && isFooterPresent ? <Divider /> : null}
              {isFooterPresent ? <Box className="w-full">{footerSlot?.props.children}</Box> : null}
            </>
          )}
        </Stack>
      </Box>
    );
  },
) as CardLayoutComponent;

CardLayout.Slot = Slot;
export { CardLayout, CardLayoutSlots };
